import { getCurrentIsoWeek } from '../utils/dateUtils';
import { profileIndexFor } from './moneyTargetDefaults';
import { getMoneyTarget } from './yearPlanTargetService';
import type {
  OverviewData,
  OverviewLocation,
  OverviewMetric,
  OverviewSeats
} from '../types/overviewTypes';

// Stand-in for the endpoint REH-213 will provide. It serves the OverviewData
// shape exactly, so the shape the prototype consumes IS the API suggestion that
// goes onto the backend card — the pattern agreed 2026-08-11 for building ahead
// of a backend.
//
// Named mock* deliberately: that prefix is what the coverage config excludes, so
// figures that exist only until REH-213 are not measured as production code and
// do not have to be tested as if they were.
//
// Figures match the encodings signed off in the design comparison, so the
// running prototype and the decision record show the same numbers.

const FULL_YEAR_WEEK_COUNT = 52;

interface LocationProfile {
  // No target: it is supplied per location by the shared store, not by the
  // profile, so a figure here would be overwritten and mislead.
  money: Omit<OverviewMetric, 'target'>;
  seats: OverviewSeats;
  completionDependentMoney: number;
}

// Three profiles, picked by a hash of the location id rather than its position in
// the list, so a location keeps the same figures wherever it is read from. The
// target is not one of them: it comes from the shared store, which is also what
// the year plan reads, so a target edited there shows on these cards too.
const CURRENT_YEAR_PROFILES: LocationProfile[] = [
  {
    money: {
      lockedPotential: 9_100_000,
      openPotential: 3_300_000,
      realised: 7_900_000
    },
    seats: { planned: 184, completed: 116 },
    completionDependentMoney: 1_800_000
  },
  {
    money: {
      lockedPotential: 10_000_000,
      openPotential: 7_400_000,
      realised: 9_600_000
    },
    seats: { planned: 262, completed: 132 },
    completionDependentMoney: 2_100_000
  },
  {
    // No target typed in. Sandbox locations land here too, which is how they stay
    // out of the overview's claims without reviving the location flag deleted on
    // 2026-08-06.
    money: { lockedPotential: 9_800_000, openPotential: 4_300_000, realised: 9_600_000 },
    seats: { planned: 212, completed: 148 },
    completionDependentMoney: 1_500_000
  }
];

// An elapsed year has no open weeks left, so potential is entirely locked and the
// only live question is how much of it landed.
const ELAPSED_YEAR_PROFILES: LocationProfile[] = [
  {
    money: { lockedPotential: 16_050_000, openPotential: 0, realised: 15_880_000 },
    seats: { planned: 238, completed: 231 },
    completionDependentMoney: 2_400_000
  },
  {
    money: { lockedPotential: 17_200_000, openPotential: 0, realised: 16_400_000 },
    seats: { planned: 259, completed: 244 },
    completionDependentMoney: 2_600_000
  },
  {
    money: { lockedPotential: 13_400_000, openPotential: 0, realised: 12_900_000 },
    seats: { planned: 205, completed: 196 },
    completionDependentMoney: 1_900_000
  }
];



const EMPTY_METRIC: Omit<OverviewMetric, 'target'> = {
  lockedPotential: 0,
  openPotential: 0,
  realised: 0
};

const EMPTY_SEATS: OverviewSeats = { planned: 0, completed: 0 };

// A year ahead of the current one has nothing planned yet. A location that has a
// target still shows it, which is the January case worth designing for: the
// ceiling is known and the plan against it is still empty.
function buildFutureLocation(locationId: string, year: number): OverviewLocation {
  return {
    locationId,
    hasPlan: false,
    lockedFromWeek: 0,
    lockedToWeek: 0,
    money: { ...EMPTY_METRIC, target: getMoneyTarget(locationId, year) },
    seats: EMPTY_SEATS,
    completionDependentMoney: 0
  };
}

export function buildMockOverviewData(locationIds: string[], year: number): OverviewData {
  const currentYear = new Date().getFullYear();
  const isElapsed = year < currentYear;
  const isFuture = year > currentYear;
  const profiles = isElapsed ? ELAPSED_YEAR_PROFILES : CURRENT_YEAR_PROFILES;

  const locations = locationIds.map((locationId) => {
    if (isFuture) return buildFutureLocation(locationId, year);

    const profile = profiles[profileIndexFor(locationId)];
    return {
      locationId,
      hasPlan: true,
      lockedFromWeek: 1,
      lockedToWeek: isElapsed ? FULL_YEAR_WEEK_COUNT : getCurrentIsoWeek(),
      // Figures come from the profile, but the target comes from the shared
      // store, so one edited on a year plan shows here too.
      money: { ...profile.money, target: getMoneyTarget(locationId, year) },
      seats: profile.seats,
      completionDependentMoney: profile.completionDependentMoney
    };
  });

  return { year, locations };
}
